import { useMemo } from 'react';
import type { IDevExRawRow } from '@skystone/shared';
import { devExApi } from '@/services/devex-api';
import { useApi } from '@/hooks/useApi';
import { DataTable, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import type { ColumnDef } from '@/components/ui';
import { buildRawColumns, buildStandardEmptyMessage, buildStandardTblSubtitle, DEVEX_METRIC_SPECS, type IRawColumnSpec } from '@/lib/tbl-page-utils';
import { PageContainer } from '@/components/layout';

const DEFAULT_LIMIT = 500;
const DEFAULT_OFFSET = 0;

function getRawRowKey(row: IDevExRawRow): string {
  return [
    row.quarter,
    row.sourceTable,
    row.sourceRowId ?? 'no-id',
    row.teamRaw ?? 'no-team',
  ].join(':');
}

export function TblRawDevExQuarterlyPage() {
  const { data, loading, error } = useApi(
    () => devExApi.listRaw({ limit: DEFAULT_LIMIT, offset: DEFAULT_OFFSET }),
    [],
  );

  const columns = useMemo<ColumnDef<IDevExRawRow>[]>(
    () => buildRawColumns<IDevExRawRow>([
      { key: 'quarter', header: 'Quarter' },
      { key: 'sourceTable', header: 'Source Table' },
      { key: 'sourceRowId', header: 'Source Row ID' },
      { key: 'sourceRowIdStrategy', header: 'ID Strategy' },
      { key: 'teamRaw', header: 'Team Raw' },
      { key: 'teamNormalized', header: 'Team Normalized' },
      { key: 'teamAliasNormalized', header: 'Team Alias Normalized' },
      { key: 'teamMappingStatus', header: 'Mapping Status' },
      { key: 'teamMappingRuleType', header: 'Rule Type' },
      { key: 'teamMappingReason', header: 'Mapping Reason' },
      { key: 'mappedTeamName', header: 'Mapped Team' },
      { key: 'mappedProductName', header: 'Mapped Product' },
      ...(DEVEX_METRIC_SPECS as IRawColumnSpec<IDevExRawRow>[]),
    ]),
    [],
  );

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader title="Tbl - raw_devex_quarterly" subtitle={buildStandardTblSubtitle()} />
      <ErrorBanner message={error} />
      <DataTable
        data={data?.items ?? []}
        columns={columns}
        keyExtractor={getRawRowKey}
        enablePagination
        enableFilters
        emptyMessage={buildStandardEmptyMessage('raw devex quarterly')}
      />
    </PageContainer>
  );
}
